import { RouteRecordRaw } from 'vue-router';
import { requireProvider, requireSeeker } from '../guards/authGuards';
import { checkSitterOnboarding } from '../guards/completionGuards';
import combineGuards from '../guards/helpers';

import Adventures from '@/apps/adventures/Adventures.vue';
import Dashboard from '@/apps/adventures/dashboard/Dashboard.vue';
import Create from '@/apps/adventures/create/Create.vue';
import Edit from '@/apps/adventures/edit/Edit.vue';
import ViewAdventure from '@/apps/adventures/details/ViewAdventure.vue';
import Search from '@/apps/adventures/search/Search.vue';

// Adventure form components
import Category from '@/apps/adventures/forms/Category.vue';
import AgeRange from '@/apps/adventures/forms/AgeRange.vue';
import Description from '@/apps/adventures/forms/Description.vue';
import Duration from '@/apps/adventures/forms/Duration.vue';
import Supplies from '@/apps/adventures/forms/Supplies.vue';
import Rate from '@/apps/adventures/forms/Rate.vue';
import Passion from '@/apps/adventures/forms/Passion.vue';
import Photo from '@/apps/adventures/forms/Photo.vue';
import Title from '@/apps/adventures/forms/Title.vue';
import Availability from '@/apps/adventures/forms/Availability.vue';
import AvailabilityDetails from '@/apps/adventures/forms/availability/AvailabilityDetails.vue';
import AvailabilityEdit from '@/apps/adventures/forms/availability/AvailabilityEdit.vue';
import Review from '@/apps/adventures/create/review/Review.vue';

/**
 * Adventures route guards by audience:
 *
 * - Provider (sitter): dashboard, create flow, edit flow — must be a sitter with completed onboarding.
 * - Seeker (parent): search — must be a parent; sitters are redirected to the provider dashboard.
 * - Public (unauthenticated or either role): adventure details — no route guard;
 *   ViewAdventure handles sitter vs parent UI (edit/activate vs contact) based on the signed-in account.
 */

/** Sitter-only routes: redirects parents to the seeker dashboard. */
const adventureProviderGuards = combineGuards([requireProvider, checkSitterOnboarding]);

const buildAdventureFormStepRoutes = ({ includeRouteNames = true }: { includeRouteNames?: boolean } = {}): RouteRecordRaw[] => [
  {
    path: 'category',
    ...(includeRouteNames ? { name: 'Category' } : {}),
    meta: { step: 1, trackCreateFlow: true },
    component: Category,
  },
  {
    path: 'age_range',
    ...(includeRouteNames ? { name: 'Age' } : {}),
    meta: { step: 2, trackCreateFlow: true },
    component: AgeRange,
  },
  {
    path: 'description',
    ...(includeRouteNames ? { name: 'Description' } : {}),
    meta: { step: 3, trackCreateFlow: true },
    component: Description,
  },
  {
    path: 'passion',
    ...(includeRouteNames ? { name: 'Promote' } : {}),
    meta: { step: 4, trackCreateFlow: true },
    component: Passion,
  },
  {
    path: 'title',
    ...(includeRouteNames ? { name: 'Title' } : {}),
    meta: { step: 5, trackCreateFlow: true },
    component: Title,
  },
  {
    path: 'duration',
    ...(includeRouteNames ? { name: 'Duration' } : {}),
    meta: { step: 6, trackCreateFlow: true },
    component: Duration,
  },
  {
    path: 'supplies',
    ...(includeRouteNames ? { name: 'Supplies' } : {}),
    meta: { step: 7, trackCreateFlow: true },
    component: Supplies,
  },
  {
    path: 'rate',
    ...(includeRouteNames ? { name: 'Rate' } : {}),
    meta: { step: 8, trackCreateFlow: true },
    component: Rate,
  },
  {
    path: 'photo',
    ...(includeRouteNames ? { name: 'Photo' } : {}),
    meta: { step: 9, trackCreateFlow: true },
    component: Photo,
  },
  {
    path: 'availability',
    component: Availability,
    meta: { step: 10 },
    children: [
      {
        path: '',
        ...(includeRouteNames ? { name: 'AdventuresAvailability' } : {}),
        component: AvailabilityDetails,
        meta: { trackCreateFlow: true },
      },
      {
        path: 'edit',
        ...(includeRouteNames ? { name: 'AvailabilityEdit' } : {}),
        component: AvailabilityEdit,
      },
    ],
  },
];

const adventuresRoutes: RouteRecordRaw[] = [
  {
    path: '/adventures',
    component: Adventures,
    meta: { shouldHideFooter: true },
    children: [
      // Provider (sitter): dashboard
      {
        path: '',
        name: 'AdventuresDashboard',
        component: Dashboard,
        beforeEnter: adventureProviderGuards,
      },
      // Seeker (parent): search
      {
        path: 'search/:pathMatch(.*)*',
        name: 'SearchAdventures',
        component: Search,
        beforeEnter: requireSeeker,
      },
      // Public: adventure detail (parents, sitters, or logged out)
      {
        path: 'details/:stateCode?/:city?/:adventureId',
        name: 'ViewAdventure',
        component: ViewAdventure,
      },
      // Provider (sitter): create flow — static segment before :adventureId/edit
      {
        path: 'new',
        name: 'CreateAdventure',
        component: Create,
        redirect: '/adventures/new/category',
        beforeEnter: adventureProviderGuards,
        children: [
          ...buildAdventureFormStepRoutes(),
          {
            path: 'review',
            name: 'Review',
            meta: { step: 11, trackCreateFlow: true },
            component: Review,
          },
        ],
      },
      // Provider (sitter): edit hub and form steps
      {
        path: ':adventureId/edit',
        component: Create,
        meta: { mode: 'edit' },
        beforeEnter: adventureProviderGuards,
        children: [
          {
            path: '',
            name: 'EditAdventure',
            component: Edit,
          },
          ...buildAdventureFormStepRoutes({ includeRouteNames: false }),
        ],
      },
    ],
  },
];

export default adventuresRoutes;
